import React, { useState } from 'react';
import { usePDF } from '../context/PDFContext';
import { GripVertical, Copy, Trash2, RotateCw, FileOutput } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from './ui/context-menu';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeftSidebar = () => {
  const { pages, setPages, selectedPages, setSelectedPages, pdfInfo, setPdfInfo, setIsProcessing } = usePDF();
  const [draggedPage, setDraggedPage] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const togglePageSelection = (pageNum) => {
    setSelectedPages((prev) =>
      prev.includes(pageNum) ? prev.filter((p) => p !== pageNum) : [...prev, pageNum]
    );
  };

  const scrollToPage = (pageNum) => {
    const el = document.querySelector(`[data-testid="pdf-page-${pageNum}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const refreshPages = async (newId) => {
    const resp = await axios.get(`${API}/pdf/pages/${newId}`);
    setPages(resp.data.pages);
    setSelectedPages([]);
  };

  // --- Drag & Drop Reorder ---
  const handleDragStart = (e, pageNum) => {
    setDraggedPage(pageNum);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedPage(null);
    setDropTarget(null);
  };
  const handleDragOver = (e, pageNum) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (pageNum !== draggedPage) setDropTarget(pageNum);
  };
  const handleDragLeave = () => setDropTarget(null);

  const handleDrop = async (e, targetPageNum) => {
    e.preventDefault();
    setDropTarget(null);
    if (!draggedPage || draggedPage === targetPageNum || !pdfInfo) return;

    const currentOrder = pages.map(p => p.page_number);
    const dragIdx = currentOrder.indexOf(draggedPage);
    const dropIdx = currentOrder.indexOf(targetPageNum);
    if (dragIdx === -1 || dropIdx === -1) return;

    const newOrder = [...currentOrder];
    newOrder.splice(dragIdx, 1);
    newOrder.splice(dropIdx, 0, draggedPage);

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('order', newOrder.join(','));
      const response = await axios.post(`${API}/pdf/reorder`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      await refreshPages(response.data.id);
      toast.success('Pages reordered');
    } catch (error) {
      toast.error('Failed to reorder: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
      setDraggedPage(null);
    }
  };

  // --- Context Menu Actions ---
  const handleDuplicate = async (pageNum) => {
    if (!pdfInfo) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('page_num', pageNum);
      const response = await axios.post(`${API}/pdf/duplicate-page`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id, num_pages: response.data.num_pages });
      await refreshPages(response.data.id);
      toast.success(`Page ${pageNum} duplicated`);
    } catch (error) {
      toast.error('Failed to duplicate: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeletePage = async (pageNum) => {
    if (!pdfInfo) return;
    if (pages.length <= 1) {
      toast.error("Can't delete the only page");
      return;
    }
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('pages', String(pageNum));
      const response = await axios.post(`${API}/pdf/delete-pages`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      await refreshPages(response.data.id);
      toast.success(`Page ${pageNum} deleted`);
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRotatePage = async (pageNum) => {
    if (!pdfInfo) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('pages', String(pageNum));
      formData.append('angle', 90);
      const response = await axios.post(`${API}/pdf/rotate`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      await refreshPages(response.data.id);
      toast.success(`Page ${pageNum} rotated 90`);
    } catch (error) {
      toast.error('Failed to rotate: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExtractPage = async (pageNum) => {
    if (!pdfInfo) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('pages', String(pageNum));
      const response = await axios.post(`${API}/pdf/split`, formData);
      // Download the extracted page
      const fileId = response.data.files?.[0]?.id;
      if (fileId) {
        window.open(`${API}/download/${fileId}`, '_blank');
        toast.success(`Page ${pageNum} extracted & downloading`);
      }
    } catch (error) {
      toast.error('Failed to extract: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  if (!pages || pages.length === 0) return null;

  return (
    <div
      data-testid="left-sidebar"
      className="w-64 border-r border-border bg-accent/50 flex flex-col h-full"
    >
      <div className="p-4 border-b border-border">
        <h3 className="text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground">
          Pages ({pages.length})
        </h3>
        <p className="text-xs text-muted-foreground mt-1">Right-click for options</p>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {pages.map((page) => (
            <ContextMenu key={page.page_number}>
              <ContextMenuTrigger asChild>
                <div
                  data-testid={`page-thumbnail-${page.page_number}`}
                  draggable={pages.length > 1}
                  onDragStart={(e) => handleDragStart(e, page.page_number)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, page.page_number)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, page.page_number)}
                  onClick={() => {
                    scrollToPage(page.page_number);
                    togglePageSelection(page.page_number);
                  }}
                  className={`relative group cursor-pointer rounded-md border-2 transition-all duration-200 ${
                    dropTarget === page.page_number
                      ? 'border-primary bg-primary/10 scale-[1.02]'
                      : selectedPages.includes(page.page_number)
                        ? 'border-primary bg-primary/5'
                        : draggedPage === page.page_number
                          ? 'border-dashed border-muted-foreground opacity-50'
                          : 'border-border hover:border-primary/50'
                  }`}
                >
                  {dropTarget === page.page_number && draggedPage && (
                    <div className="absolute -top-1.5 left-2 right-2 h-0.5 bg-primary rounded-full" />
                  )}
                  <div className="p-2">
                    <img
                      src={page.thumbnail}
                      alt={`Page ${page.page_number}`}
                      className="w-full h-auto rounded shadow-sm pointer-events-none"
                      draggable={false}
                    />
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        Page {page.page_number}
                      </span>
                      <GripVertical className={`w-4 h-4 text-muted-foreground transition-opacity ${pages.length > 1 ? 'opacity-50 group-hover:opacity-100 cursor-grab' : 'opacity-0'}`} />
                    </div>
                  </div>
                </div>
              </ContextMenuTrigger>

              <ContextMenuContent data-testid={`context-menu-page-${page.page_number}`} className="w-48">
                <ContextMenuItem
                  data-testid={`ctx-duplicate-${page.page_number}`}
                  onClick={() => handleDuplicate(page.page_number)}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" /> Duplicate Page
                </ContextMenuItem>
                <ContextMenuItem
                  data-testid={`ctx-rotate-${page.page_number}`}
                  onClick={() => handleRotatePage(page.page_number)}
                  className="gap-2"
                >
                  <RotateCw className="w-4 h-4" /> Rotate 90
                </ContextMenuItem>
                <ContextMenuItem
                  data-testid={`ctx-extract-${page.page_number}`}
                  onClick={() => handleExtractPage(page.page_number)}
                  className="gap-2"
                >
                  <FileOutput className="w-4 h-4" /> Extract Page
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem
                  data-testid={`ctx-delete-${page.page_number}`}
                  onClick={() => handleDeletePage(page.page_number)}
                  disabled={pages.length <= 1}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4" /> Delete Page
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default LeftSidebar;
